import React, { useEffect, useMemo, useState } from 'react';
import { Modal, FormLayout, TextField, InlineStack, Badge, Text, BlockStack, Select } from '@shopify/polaris';
import { configApi } from '../../../services/api/configApi';

const DEFAULTS = {
  sandbox: {
    baseUrl: 'https://sandbox.api.wayfair.com',
    audience: 'https://sandbox.api.wayfair.com/'
  },
  production: {
    baseUrl: 'https://api.wayfair.com',
    audience: 'https://api.wayfair.com/'
  }
};

export function WayfairCredentialsModal({ open, onClose, onSave, config = {} }) {
  const [formData, setFormData] = useState({
    connectionName: 'Wayfair',
    environment: 'sandbox',
    baseUrl: DEFAULTS.sandbox.baseUrl,
    tokenUrl: 'https://sso.auth.wayfair.com/oauth/token',
    audience: DEFAULTS.sandbox.audience,
    clientId: '',
    clientSecret: '',
    supplierId: '',
    testPath: '/v1/demo/clock'
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [status, setStatus] = useState({ ok: null, message: '' });

  useEffect(() => {
    if (!open) return;
    const env = config.environment === 'production' ? 'production' : 'sandbox';
    setFormData({
      connectionName: config.connectionName || 'Wayfair',
      environment: env,
      baseUrl: config.baseUrl || DEFAULTS[env].baseUrl,
      tokenUrl: config.tokenUrl || 'https://sso.auth.wayfair.com/oauth/token',
      audience: config.audience || DEFAULTS[env].audience,
      clientId: config.clientId || '',
      clientSecret: config.clientSecret || '',
      supplierId: config.supplierId || '',
      testPath: config.testPath || '/v1/demo/clock'
    });
    setStatus({ ok: null, message: '' });
  }, [open, config]);

  const canSave = useMemo(() => (
    !!formData.connectionName.trim() &&
    !!formData.baseUrl.trim() &&
    !!formData.tokenUrl.trim() &&
    !!formData.audience.trim() &&
    !!formData.clientId.trim() &&
    !!formData.clientSecret.trim()
  ), [formData]);

  const updateEnvironment = (environment) => {
    setFormData((prev) => ({
      ...prev,
      environment,
      baseUrl: DEFAULTS[environment].baseUrl,
      audience: DEFAULTS[environment].audience
    }));
  };

  const payload = () => ({
    connectionName: formData.connectionName.trim(),
    environment: formData.environment,
    baseUrl: formData.baseUrl.trim().replace(/\/$/, ''),
    tokenUrl: formData.tokenUrl.trim(),
    audience: formData.audience.trim(),
    clientId: formData.clientId.trim(),
    clientSecret: formData.clientSecret === '****' ? undefined : formData.clientSecret,
    supplierId: formData.supplierId.trim(),
    testPath: formData.testPath.trim() || '/v1/demo/clock'
  });

  const handleSave = async () => {
    setSaving(true);
    setStatus({ ok: null, message: '' });
    try {
      await configApi.saveWayfairCredentials(payload());
      setStatus({ ok: true, message: 'Connection saved' });
      if (onSave) await onSave();
      onClose();
    } catch (error) {
      setStatus({ ok: false, message: error.message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setStatus({ ok: null, message: 'Testing connection...' });
    try {
      const result = await configApi.validateCredentials('wayfair', payload());
      if (result.success && result.valid) {
        setStatus({ ok: true, message: result.message || 'Connection successful' });
      } else {
        setStatus({ ok: false, message: result.error || 'Connection failed' });
      }
    } catch (error) {
      setStatus({ ok: false, message: error.message || 'Connection failed' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Wayfair Connection"
      primaryAction={{
        content: 'Save',
        onAction: handleSave,
        disabled: !canSave,
        loading: saving
      }}
      secondaryActions={[
        { content: 'Test connection', onAction: handleTest, disabled: !canSave || testing, loading: testing },
        { content: 'Cancel', onAction: onClose }
      ]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          <FormLayout>
            <TextField
              label="Connection name"
              value={formData.connectionName}
              onChange={(value) => setFormData(prev => ({ ...prev, connectionName: value }))}
              autoComplete="off"
            />
            <Select
              label="Environment"
              options={[
                { label: 'Sandbox', value: 'sandbox' },
                { label: 'Production', value: 'production' }
              ]}
              value={formData.environment}
              onChange={updateEnvironment}
            />
            <TextField
              label="Client ID"
              value={formData.clientId}
              onChange={(value) => setFormData(prev => ({ ...prev, clientId: value }))}
              autoComplete="off"
            />
            <TextField
              label="Client Secret"
              value={formData.clientSecret}
              onChange={(value) => setFormData(prev => ({ ...prev, clientSecret: value }))}
              type="password"
              autoComplete="off"
              helpText={config.clientSecret === '****' ? 'Stored; leave as **** to keep unchanged' : 'From Wayfair Developer application details'}
            />
            <TextField
              label="Supplier ID"
              value={formData.supplierId}
              onChange={(value) => setFormData(prev => ({ ...prev, supplierId: value }))}
              autoComplete="off"
            />
            <TextField
              label="API URL"
              value={formData.baseUrl}
              onChange={(value) => setFormData(prev => ({ ...prev, baseUrl: value }))}
              autoComplete="off"
            />
            <TextField
              label="Audience"
              value={formData.audience}
              onChange={(value) => setFormData(prev => ({ ...prev, audience: value }))}
              autoComplete="off"
            />
            <TextField
              label="Token URL"
              value={formData.tokenUrl}
              onChange={(value) => setFormData(prev => ({ ...prev, tokenUrl: value }))}
              autoComplete="off"
            />
            <TextField
              label="Test path"
              value={formData.testPath}
              onChange={(value) => setFormData(prev => ({ ...prev, testPath: value }))}
              autoComplete="off"
            />
          </FormLayout>

          {status.message && (
            <InlineStack gap="200">
              <Badge tone={status.ok ? 'success' : status.ok === false ? 'critical' : 'info'}>
                {status.ok ? 'Connected' : status.ok === false ? 'Failed' : 'Testing'}
              </Badge>
              <Text as="span">{status.message}</Text>
            </InlineStack>
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}

export default WayfairCredentialsModal;
